/**
 * Issue #166 FAC-11 — the ARCHIVED state, as the client reads it.
 *
 * The server is the boundary: the default reads that feed the pickers
 * (`GET /grounds`, `GET /grounds/facilities`, `GET /:id/facilities`) exclude
 * archived rows at the query, and the attach paths refuse them
 * (`ground_archived` / `facility_archived`). What this module holds is the
 * client's OWN half of the same rule, for the two suggestion sources that
 * legitimately still carry archived rows because they are personal or
 * historical reads, not directory reads:
 *
 *   - `GET /players/me/grounds` — a bookmark of an archived venue keeps (it
 *     is the player's pointer, and silently dropping it would hide where
 *     their saved ground went);
 *   - `GET /venues/played` — "where have I played" is history, and history
 *     keeps the venue.
 *
 * Both travel with `archived_at` so `GroundPicker`, which merges them in as
 * suggestions, can drop what it must not OFFER without either endpoint
 * rewriting what happened. This is the same belt-and-braces shape as
 * `facilitiesByGround`'s second gate: the picker cannot offer a ground the
 * save would refuse, even if a future endpoint change widens a source.
 *
 * A rendering surface (the map page asks for archived rows explicitly and
 * badges them) uses `isArchived` for the badge; the label is deliberately
 * one calm word — "Archived" says the state, and WHEN it happened is in the
 * timestamp for any surface that needs it.
 */

/** `true` when a ground/facility API row is archived. Reads the API's own
 * field name; a row with no `archived_at` key is simply not archived. */
export function isArchived(row) {
  return Boolean(row?.archived_at);
}

/** The rows a PICKER may offer: everything not archived. Nullish rows are
 * dropped too — a picker cannot offer a hole. */
export function excludeArchived(rows) {
  return (rows || []).filter((row) => row && !isArchived(row));
}

/** The badge text, one place, so every surface says it the same way. */
export const ARCHIVED_LABEL = "Archived";
